import { BrandCode, CarouselsResponse, CategoryRequest, PlacementsResponse, SearchParams } from '@/types/algonomy';

import { ProductService } from './services';
import {
  ALGONOMY_MOBILE_PLACEMENTS,
  ALGONOMY_PLACEMENTS,
  DeleteProductRequest,
  DISCONTINUED_ALGONOMY_PLACEMENTS,
  DISCONTINUED_ALGONOMY_PLACEMENTS_MOBILE,
  ProductDetails,
  ProductPageViewRequest,
} from './types';
import ApiClient from '@/lib/ApiClient';
import Result, { EmptyResult, RedirectResult, HttpResult } from '@/lib/Result';
import { ProductMetaData } from '@/types/productPage';
import AlgonomyConverter from '@/converters/algonomyConverter';
import ShippingEstimatorDTO from '@/dto/shippingEstimatorDTO';
import AlgonomyApiBase from '@/lib/Algonomy/services/AlgonomyApiBase/AlgonomyApiBase';

class ProductApiService extends AlgonomyApiBase implements ProductService {
  private readonly algonomyConverter: AlgonomyConverter;

  public constructor(
    protected apiClient: ApiClient,
  ) {
    super(apiClient);
    this.algonomyConverter = new AlgonomyConverter();
  }

  public async getProduct(
    productName: string,
    typeCode: string,
    productId: string,
    subProductId?: string,
    cookie?: string,
  ): Promise<Result<ProductDetails | null>> {
    try {
      const urlParams = new URLSearchParams([
        ['productName', productName],
        ['typeCode', typeCode],
        ['productId', productId],
      ]);
      if (subProductId) {
        urlParams.append('subProductId', subProductId);
      }
      const productDetailsResult = await this.apiClient.get(`/product?${urlParams}`, {
        headers: cookie ? { Cookie: cookie } : undefined,
        redirect: 'manual',
      });

      if (productDetailsResult.status === 301 || productDetailsResult.status === 302) {
        const location = productDetailsResult.headers.get('Location') || '';
        return new RedirectResult(location, productDetailsResult.status);
      }
      const json = await productDetailsResult.json();
      if (json.shippingRateResponses) {
        const totalRetail = await this.apiClient.get('/content/promotion-timers/FreeSHTotalRetail');
        const RetailJson = await totalRetail.json();
        const shippingEstimatorTransformed = new ShippingEstimatorDTO(json.shippingRateResponses, RetailJson?.content);
        json.shippingRateResponses = shippingEstimatorTransformed.getShippingRateResponses;
      }
      return new Result(json);
    } catch (error) {
      console.error('Error fetching product details', error);
      throw error;
    }
  }

  public async getReccomendedProducts(
    subProductSku: string,
    discontinued: boolean,
    isMobile: boolean,
    searchParams: SearchParams,
    headersList: Headers,
    cookie?: string,
  ): Promise<HttpResult<{ topCarousels: PlacementsResponse; bottomCarousels: PlacementsResponse }>> {
    const topCarousels: PlacementsResponse = [];
    const bottomCarousels: PlacementsResponse = [];
    try {
      

      if (!discontinued) {
        const urlParams = new URLSearchParams([
          ['brandCode', BrandCode.PYR],
          ['productId', subProductSku],
          ['placements', isMobile ? ALGONOMY_MOBILE_PLACEMENTS : ALGONOMY_PLACEMENTS],
          ['count', '15'],
        ]);

        const prefValue = this.getUtmValues(searchParams, headersList);
        if(prefValue) {
          urlParams.append('pref', prefValue)
        }

        const searchResult = await this.apiClient.get(`recsForPlacements?${urlParams}`, {
          headers: cookie ? { Cookie: cookie } : undefined,
        });
        const json: CarouselsResponse = await searchResult.json();
        const converted: CategoryRequest = this.algonomyConverter.convertCarouselProduct(json);
        converted.placements?.forEach((placement) => {
          if (placement.placement[placement.placement.length - 1] <= '3') {
            topCarousels.push(placement);
          } else {
            bottomCarousels.push(placement);
          }
        });
        return new HttpResult({ topCarousels, bottomCarousels }, searchResult.headers);
      } else {
        return new HttpResult({ topCarousels, bottomCarousels });
      }
    } catch (error) {
      return new HttpResult({ topCarousels, bottomCarousels });
    }
  }

  public async getDiscontinuedRecommendedProducts(
    subProductSku: string,
    discontinued: boolean,
    isMobile: boolean,
    searchParams: SearchParams,
    headersList: Headers,
    cookie?: string,
  ): Promise<HttpResult<{ topDiscCarousels: PlacementsResponse; bottomDiscCarousels: PlacementsResponse }>> {
    const topDiscCarousels: PlacementsResponse = [];
    const bottomDiscCarousels: PlacementsResponse = [];
    try {
      if (discontinued) {
        const urlParams = new URLSearchParams([
          ['brandCode', BrandCode.PYR],
          ['productId', subProductSku],
          ['placements', isMobile ? DISCONTINUED_ALGONOMY_PLACEMENTS_MOBILE : DISCONTINUED_ALGONOMY_PLACEMENTS],
          ['count', '15'],
        ]);
        const prefValue = this.getUtmValues(searchParams, headersList);
        if(prefValue) {
          urlParams.append('pref', prefValue)
        }
        const searchResult = await this.apiClient.get(`recsForPlacements?${urlParams}`, {
          headers: cookie ? { Cookie: cookie } : undefined,
        });
        const json: CarouselsResponse = await searchResult.json();
        const converted: CategoryRequest = this.algonomyConverter.convertCarouselProduct(json);
        converted.placements?.forEach((product) => {
          if (product.placement === 'item_page.mrroos' || product.placement === 'item_page.rroos') {
            topDiscCarousels.push(product);
          } else {
            bottomDiscCarousels.push(product);
          }
        });
        return new HttpResult({ topDiscCarousels, bottomDiscCarousels }, searchResult.headers);
      }
      return new HttpResult({ topDiscCarousels, bottomDiscCarousels });
    } catch (error) {
      return new HttpResult({ topDiscCarousels, bottomDiscCarousels });
    }
  }

  public async getProductMetaData(
    typeCode: string,
    productId: string,
    subProductId: string,
  ): Promise<ProductMetaData | null> {
    try {
      const urlParams = new URLSearchParams([
        ['typeCode', typeCode],
        ['productId', productId],
      ]);
      if (subProductId) {
        urlParams.append('subProductId', subProductId);
      }

      const metaData = await this.apiClient.get(`/metatags/product?${urlParams}`);
      const json = await metaData.json();
      json.metadataBase = new URL('https://www.pyramydair.com');
      return json;
    } catch (error) {
      return null;
    }
  }

  public async deleteProduct(request: DeleteProductRequest): Promise<EmptyResult> {
    const { productItemId, orderId } = request;

    await this.apiClient.delete(`/shoppingCart/products/${productItemId}`, {
      body: { orderId },
    });

    return new EmptyResult();
  }

  public async productPageView(request: ProductPageViewRequest): Promise<EmptyResult> {
    try {
      await this.apiClient.post('/page-view/product', {
        body: request,
      });
    } catch (error) {
      console.error('Cant increment product page view', request);
    } finally {
      return new EmptyResult();
    }
  }
}

export default ProductApiService;
